import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BarGraphChannel, DefaultGraphStyle } from '@models/graphs';
import { DeviceFlagsService } from '@service/device-flags.service';
import { GraphsService } from '@service/graphs.service';
import { ECharts, EChartsOption } from 'echarts';
import { BarChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { AlarmLevel } from 'ops/common/models/alarm-level';
import { Channel, FlagOption } from 'ops/common/models/channel';
import { BarGraph } from 'ops/common/models/graphs';

const alarmLevelBarColor = new Map();
alarmLevelBarColor.set(AlarmLevel.Warning, DefaultGraphStyle.alarmWarning);
alarmLevelBarColor.set(AlarmLevel.Caution, DefaultGraphStyle.alarmCaution);
alarmLevelBarColor.set(AlarmLevel.Alarm, DefaultGraphStyle.alarm);

const alarmLevelBarHoverColor = new Map();
alarmLevelBarHoverColor.set(AlarmLevel.Warning, DefaultGraphStyle.alarmWarningHover);
alarmLevelBarHoverColor.set(AlarmLevel.Caution, DefaultGraphStyle.alarmCautionHover);
alarmLevelBarHoverColor.set(AlarmLevel.Alarm, DefaultGraphStyle.alarmHover);

@Component({
    selector: 'app-bar-graph',
    templateUrl: './bar-graph.component.html',
    styleUrls: ['./bar-graph.component.css'],
    standalone: false,
})
export class BarGraphComponent implements OnChanges {
    readonly echartsExtentions: any[];
    echartsOptions: EChartsOption = {};
    chart: ECharts = null;

    @Input() channels: { [key: string | number]: Channel };
    @Input() graph: BarGraph;
    @Input() title: string;

    graphChannels: BarGraphChannel[] = [];
    alarmBarColorMap = new Map();
    graphYAxisMax = 0;

    constructor(
        private flagService: DeviceFlagsService,
        private graphService: GraphsService,
    ) {
        this.echartsExtentions = [BarChart, TooltipComponent, GridComponent, LegendComponent];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.channels) return;
        if (changes.graph) {
            this.graphChannels = [...this.createBarChannels()];
            this.initialAlarmBgColor();
            this.updateChartOption();
        }
    }

    ngOnInit() {
        this.graphChannels = [...this.createBarChannels()];

        this.flagService.onAllFlagsUpdated().subscribe(() => {
            this.graphChannels = [...this.createBarChannels()];
            this.updateChartOption();
        });
        this.initialAlarmBgColor();
        this.findMaxYAxis();
        this.initEchartOption();
    }

    initEchartOption() {
        this.echartsOptions = {
            width: '95%',
            color: [DefaultGraphStyle.blueColor],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
                backgroundColor: DefaultGraphStyle.tooltipBgColor,
                borderColor: DefaultGraphStyle.tooltipBorderColor,
                formatter: (params) => {
                    let result = '';
                    params.forEach((item) => {
                        result +=
                            this.graphService.getTootipBlueText(item.name) +
                            this.graphService.getTootipBlueTextBold(item.data.channel) +
                            this.graphService.getTootipWhiteText(`${item.value}&#8451`);
                    });
                    return result;
                },
                padding: 8,
            },
            xAxis: [
                {
                    type: 'category',
                    data: this.graphService.generateTitleArr(this.graphChannels),
                    axisPointer: {
                        type: 'shadow',
                    },
                    position: 'bottom',
                    axisTick: {
                        show: false,
                    },
                },
                {
                    type: 'category',
                    data: this.graphService.generateValueArr(this.graphChannels),
                    axisPointer: {
                        type: 'shadow',
                    },
                    position: 'bottom',
                    offset: 30,
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                },
            ],
            yAxis: [
                {
                    type: 'value',

                    max: this.graphYAxisMax,
                    interval: this.graphYAxisMax / this.graph.divisions,
                    axisLine: {
                        lineStyle: {
                            color: DefaultGraphStyle.yAxisColor,
                        },
                    },
                    splitLine: {
                        lineStyle: {
                            color: DefaultGraphStyle.yAxisColor,
                        },
                    },
                    axisLabel: {
                        fontSize: DefaultGraphStyle.fontSize,
                        formatter: (value) => {
                            return value.toFixed();
                        },
                    },
                },
            ],
            grid: {
                left: '3%',
                tooltip: {
                    axisPointer: {
                        shadowStyle: {
                            color: DefaultGraphStyle.hoverBgColor,
                        },
                    },
                },
            },
            series: [
                {
                    data: this.generateSeriesValueArr(),
                    type: 'bar',
                    barMaxWidth: 100,
                },
            ],
        };
    }

    updateChartOption() {
        let newseries = this.echartsOptions;
        newseries.xAxis[0].data = this.graphService.generateTitleArr(this.graphChannels);
        newseries.xAxis[1].data = this.graphService.generateValueArr(this.graphChannels);
        newseries.yAxis[0].interval = this.graphYAxisMax / this.graph.divisions;
        newseries.series[0].data = this.generateSeriesValueArr();

        this.echartsOptions = newseries;
        this.chart.setOption(this.echartsOptions);
    }

    onChartInit(event: ECharts) {
        this.chart = event;
    }

    generateSeriesValueArr() {
        let values = [];
        for (let i = 0; i < this.graphChannels.length; i++) {
            let status = this.flagService.getStatus(this.graphChannels[i].channel);
            values[i] = {
                value: this.graphChannels[i].value,
                channel: this.graphChannels[i].channel.opsData.name,
                itemStyle: {
                    color: this.alarmBarColorMap
                        .get(this.graphChannels[i].channel.fcuData.fcuChannelId)
                        .get(status)[0],
                },
                emphasis: {
                    itemStyle: {
                        color: this.alarmBarColorMap
                            .get(this.graphChannels[i].channel.fcuData.fcuChannelId)
                            .get(status)[1],
                    },
                },
            };
        }

        return values;
    }

    createBarChannels() {
        let barGraphChannels = [];
        for (let i = 0; i < this.graph.channels.length; i++) {
            let id = this.graph.channels[i].fcuChannelId;
            barGraphChannels[i] = this.getBarGraphChannel(id, i);
        }
        return barGraphChannels;
    }

    getBarGraphChannel(id: number, i: number) {
        return {
            channel: this.channels[id],
            title: this.graph.channels[i].title,
            value: this.flagService.getFlags(this.channels[id])?.value
                ? this.flagService.getFlags(this.channels[id])?.value
                : 0,
        };
    }

    initialAlarmBgColor() {
        this.alarmBarColorMap = new Map();
        for (let i = 0; i < this.graphChannels.length; i++) {
            this.alarmBarColorMap.set(
                this.graphChannels[i].channel.fcuData.fcuChannelId,
                this.createMap(this.graphChannels[i].channel),
            );
        }
    }

    createMap(channel: Channel) {
        let map = new Map();

        map.set(FlagOption.LOW_LOW, [
            alarmLevelBarColor.get(channel.fcuData.alarmSettingVeryLow.alarmLevel),
            alarmLevelBarHoverColor.get(channel.fcuData.alarmSettingVeryLow.alarmLevel),
        ]);
        map.set(FlagOption.LOW, [
            alarmLevelBarColor.get(channel.fcuData.alarmSettingLow.alarmLevel),
            alarmLevelBarHoverColor.get(channel.fcuData.alarmSettingLow.alarmLevel),
        ]);
        map.set(FlagOption.NORMAL, [
            DefaultGraphStyle.alarmNormal,
            DefaultGraphStyle.alarmNormalHover,
        ]);
        map.set(FlagOption.HIGH, [
            alarmLevelBarColor.get(channel.fcuData.alarmSettingHigh.alarmLevel),
            alarmLevelBarHoverColor.get(channel.fcuData.alarmSettingHigh.alarmLevel),
        ]);
        map.set(FlagOption.HIGH_HIGH, [
            alarmLevelBarColor.get(channel.fcuData.alarmSettingVeryHigh.alarmLevel),
            alarmLevelBarHoverColor.get(channel.fcuData.alarmSettingVeryHigh.alarmLevel),
        ]);
        map.set(FlagOption.UNDEFINED, [
            DefaultGraphStyle.alarmDefault,
            DefaultGraphStyle.alarmDefaultHover,
        ]);

        return map;
    }

    findMaxYAxis() {
        this.graphYAxisMax = Math.max(
            ...this.graphChannels.map((graph) => graph.channel.fcuData.measurableRange.max),
        );
    }
}
